import React from 'react';
import { Formik, Field } from 'formik';
import * as Yup from 'yup';
import isEmpty from 'lodash/isEmpty';
import { toast } from 'react-toastify';

import { useProfile } from '../../hook/useProfile';

import Thumb from '../../components/Thumb';
import FormButton from '../../components/form/button';
import FileUpload from '../../components/form/fileUpload';
import UnexpectedError from '../../components/UnexpectedError';
import BankDetails from '../../components/Profile/BankDetails';
import PersonalDetails from '../../components/Profile/PersonalDetails';
import SignatureDisplay from '../../components/Profile/SignatureDisplay';
import SignatureSetup from '../../components/Profile/SignatureSetup';

const Profile = () => {
  document.title = 'CrowdFarmer | My Profile';

  const {
    menuOpen,
    profile,
    updateProfile,
    setProfile,
    setUnmountSetup,
    unmountSetup,
    setSession,
    unexpectedError,
    setUnexpectedError
  } = useProfile();

  const { firstName, lastName, photoUrl, signature, bank } = profile;

  const placeHolder = text => {
    return <span className="text-xs opacity-25">{text}</span>;
  };

  const FileFormSchema = Yup.object().shape({
    file: Yup.mixed()
      .required('*No image file selected!')
      .test('fileSize', 'File Size is too large', value => value && value.size <= 1024 * 1024 * 2)
      .test(
        'fileType',
        'Unsupported File Format',
        value => value && ['image/jpg', 'image/jpeg', 'image/png'].includes(value.type)
      )
  });

  return (
    <>
      {unexpectedError ? (
        <UnexpectedError unexpectedError={unexpectedError} />
      ) : (
        <>
          <div className="relative container mx-auto pt-32 pb-20">
            <div
              className={`flex flex-wrap items-center lg:px-32 ${
                menuOpen ? 'pl-14 pr-2 md:pl-48' : 'px-4 md:px-8'
              }`}
            >
              <Formik
                initialValues={{ file: undefined }}
                validationSchema={FileFormSchema}
                enableReinitialize
                onSubmit={async (values, { setSubmitting, resetForm }) => {
                  let formData = new FormData();
                  if (values.file) formData.append('userImage', values.file);
                  try {
                    setSubmitting(true);
                    const response = await updateProfile(formData);
                    setSubmitting(false);
                    if (response.code === 200) {
                      setProfile(response.data);
                      toast.success(response.message, {
                        position: toast.POSITION.TOP_RIGHT
                      });
                      resetForm({});
                    } else {
                      toast.error(response.data.message, {
                        position: toast.POSITION.TOP_RIGHT
                      });
                    }
                  } catch (error) {
                    if (error && error.status === 401) setSession(false);
                    else setUnexpectedError('Unexpected Server Error!');
                  }
                }}
              >
                {({
                  values,
                  handleSubmit,
                  handleBlur,
                  handleChange,
                  isSubmitting,
                  isValid,
                  errors,
                  touched
                }) => (
                  <form
                    className="flex flex-wrap justify-center text-center md:text-left md:justify-start"
                    onSubmit={handleSubmit}
                  >
                    <div className="h-32 w-32 overflow-hidden rounded-full border-2 border-gray-600 xl:border-gray-300">
                      <Thumb
                        src={photoUrl}
                        imageFile={values.file}
                        alt="Profile Pic"
                        className="inline-block h-full w-full object-cover"
                      />
                    </div>
                    <div className="block md:ml-10">
                      <h4 className="font-bold">
                        {firstName} {lastName}
                      </h4>
                      <label className="block overflow-hidden relative">
                        <button
                          type="button"
                          className="border-2 border-cf-300 focus:outline-none text-cf-300 py-2 w-full rounded inline-flex items-center"
                        >
                          <Field
                            name="file"
                            component={FileUpload}
                            accept="image/jpeg, image/png"
                            className="opacity-0 w-full cursor-pointer absolute block"
                          />
                          <span className="mx-auto">Upload</span>
                        </button>
                      </label>
                      {errors.file && touched.file ? (
                        <p className="text-xs text-red-700">{errors.file}</p>
                      ) : null}
                      {values.file && (
                        <FormButton
                          name="Submit"
                          color="bg-cf-300"
                          btnPad="py-3 text-lg"
                          className="float-right w-1/2 mt-4"
                          loading={isSubmitting}
                        />
                      )}
                    </div>
                  </form>
                )}
              </Formik>
            </div>
          </div>
          <div
            className={`relative bg-cf-200 pb-16 lg:pl-48 lg:pr-16 ${
              menuOpen ? 'pl-14 pr-2 md:pl-48 md:pr-8' : 'px-4 md:px-8'
            }`}
          >
            <div className="bg-cf-50 py-16 relative">
              <div className="container mx-auto">
                <div className="grid grid-cols-1 md:grid-cols-2 md:gap-5 lg:gap-10">
                  <div>
                    <PersonalDetails
                      profile={profile}
                      placeHolder={placeHolder}
                      updateProfile={updateProfile}
                      setProfile={setProfile}
                      setSession={setSession}
                      setUnexpectedError={setUnexpectedError}
                    />
                  </div>
                  <div>
                    <BankDetails
                      bankDetails={bank}
                      placeHolder={placeHolder}
                      updateProfile={updateProfile}
                      setProfile={setProfile}
                      setSession={setSession}
                      setUnexpectedError={setUnexpectedError}
                    />
                  </div>
                  <div>
                    {!isEmpty(signature) && signature.string && !unmountSetup ? (
                      <SignatureDisplay
                        check={signature.check}
                        string={signature.string}
                        setUnmountSetup={setUnmountSetup}
                        unmountSetup={unmountSetup}
                        updateProfile={updateProfile}
                        setProfile={setProfile}
                        setSession={setSession}
                        setUnexpectedError={setUnexpectedError}
                      />
                    ) : (
                      <SignatureSetup
                        updateProfile={updateProfile}
                        setProfile={setProfile}
                        setUnmountSetup={setUnmountSetup}
                        unmountSetup={unmountSetup}
                        setSession={setSession}
                        setUnexpectedError={setUnexpectedError}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default Profile;
